import '@babel/polyfill';
import React, {Fragment, lazy, Suspense} from 'react'
import PropTypes from 'prop-types'
import Router from 'next/router';
import Block from '../adapters/helpers/Block';
import { customJsonstringify, getCanonicalURL } from '../adapters/helpers/Utils';
import ImageFactory from '../adapters/cloudinary/ImageFactory';
import { setViewed } from '../persistence/recentlyViewed'
import contentfulClient from '../adapters/model/ContentfulClient';
import { MetaConstants } from '../adapters/helpers/Constants';
import { BazaarVoiceConstants } from '../adapters/helpers/Constants';
import { OrganizationConstants, ComponentFactoryConstants } from '../adapters/helpers/Constants';
import { addBVScript } from '../adapters/helpers/Utils';
import { addBVProductPageCallback } from '../adapters/helpers/Utils';
import { optimizeImage } from '../adapters/helpers/pagespeedHelper';
import Script from 'next/script';
const Meta = lazy(() => import('../components/Meta/Meta'));
const GTMScriptFunction = lazy(() => import('../components/GTMScriptFunction'));
const Loader = lazy(() => import('../adapters/helpers/Loader'));
import DOMPurify from 'isomorphic-dompurify';

class Product extends React.Component {

    componentDidMount() {
        const slug = this.props?.slug;
        const product = this.getProduct();

        if(slug) {
            setViewed(slug);
        }

        Router.beforePopState(({as}) => {
            location.href = as;
        });

        addBVScript();
        addBVProductPageCallback(product.name);
    }

 static async getInitialProps({ asPath, query }) {
        const slug = query?.slug || asPath;
        const siteLocale = process.env.SITE_LOCALE;
        let languageCode, countryCode;
        if (siteLocale) {
            languageCode = siteLocale.substring(0, 2);
            countryCode = siteLocale.slice(-2);
        } else {
            const client = await contentfulClient.getClient();
            const locales = await client.getLocales();
            const defaultLocale = locales.items.find(locale => locale.default);
            countryCode = defaultLocale.code.slice(-2);
            languageCode = defaultLocale.code.substring(0, 2);
        }

        let baseProps = await this.initPage(slug);
        baseProps.countryCode = countryCode;
        baseProps.languageCode = languageCode;
        return {
            ...baseProps
        }
    }

    static async initPage(slug) {
        let product = undefined;

        // Errors pages doesn't have a page entry in Contentful CMS

        if (slug) {
            const { default: productDao } = await import('../adapters/model/dao/ProductDao');
            product = await productDao.getActiveProductBySlug(slug);
        }
        let newProducts  = product;
        newProducts?.fields?.blocks[0]?.fields?.blocks[1]?.fields?.blocks.map((blocksArr)=>{
            if(blocksArr?.fields?.contentType == ComponentFactoryConstants.ProductHighlights && blocksArr?.fields?.background?.fields){
                blocksArr.fields.background.fields.isBannerImage = true;
            }else if(blocksArr.fields.devicesConfigurations){
                blocksArr.fields.devicesConfigurations.map((devices)=>{
                    if(devices.fields.backgroundAsset && devices.fields.backgroundAsset.fields){
                        devices.fields.backgroundAsset.fields.isBannerImage = true;
                    }
                })
            }
        })
        if(newProducts?.fields?.productOverview?.fields?.mainAsset?.fields){
            newProducts.fields.productOverview.fields.mainAsset.fields.isBannerImage = true;
        }

        return {'product' : newProducts, 'slug' : slug};
    }

    constructor(props) {
        super(props);
    }

    /**
     * Rendering of the page. This always render the page using a common layout.
     *
     * Override the render method to do otherwise.
     *
     * @returns {*}
     */
    render() {
        let product = this.getProduct();
        let pagePath = product.productOverview.fields.slug.fields.slug;
        let productVariants = product.productOverview.fields.productVariants;
        let productVariant = {}
        if (productVariants) {
            productVariant = productVariants[0];
        }
        let metaData = this.getMetadata()
        if(metaData){
            metaData.pageType = 'product'
        }
        return (
            <Suspense fallback={<Loader />}>
            <Fragment>
            <Suspense fallback={<Loader />}>
                <Meta metadata={metaData} pagePath={pagePath}/>
            </Suspense>
                <div className='ob-wrapper'>
                    <Suspense fallback={<Loader />}>{this.renderBody()}</Suspense>
                </div>
                <Suspense fallback={<Loader />}>
                <GTMScriptFunction languageCode={this.props.languageCode} countryCode={this.props.countryCode} productVariant={productVariant} />
                {this.renderBazaarVoiceDynamicCatalogCollectionScript()}
                </Suspense>
            </Fragment>
            </Suspense>
        )
    }

    getProduct() {
        return this.props.product.fields;
    }

    getMetadata() {
        return this.props.product?.fields?.metadata?.fields
    }

    /**
     * By default, the page will render the modules of the Product.
     * Can be overridden on the child class to do specific rendering.
     * @returns {*|void}
     */
    renderBody() {
        return new Block(this.props.product).renderChildBlocks({ extraAttributes : {entity: this.getProduct(), slug: this.props.slug}});
    }

    getImageUrl(image) {
        let contentImageId = image?.fields?.asset?.fields?.file?.url;
        let rendition = image.fields.imageRendition;
        let aspectRatio = rendition?.fields.aspectRatio;
        let transformations = rendition?.fields.transformations;
        let format = image.fields.forcedFormat || 'auto';
        let url = contentImageId && optimizeImage(ImageFactory.buildContentfullImageUrlByWidth(contentImageId, aspectRatio, transformations, format));
        return url;
    }

    renderBazaarVoiceDynamicCatalogCollectionScript() {
        let product = this.getProduct();

        let countryCode = this.props.countryCode;

        let imageUrl = this.getImageUrl(product.productOverview.fields.mainAsset);
        let productPageUrl = getCanonicalURL(product.productOverview.fields.slug.fields.slug, MetaConstants.https);

        let productVariants = product.productOverview.fields.productVariants;
        let skus = [];
        let eans = [];
        let reviewAndRatingId;
        if (productVariants) {
            for (let i = 0; i < productVariants.length; i++) {
                let productVariant = productVariants[i];

                let sku = productVariant?.fields?.sku;
                let ean = productVariant?.fields?.ean;

                if (ean) {
                    if(this.isValidBarcode(ean)) {
                        if (ean.length == 13) {
                            eans.push(ean);
                        }
                    }
                }

                if(sku) {
                    if(this.isValidBarcode(sku)) {
                        if (sku.length == 12) {
                            skus.push(sku);
                        }
                        // EANs have been put in the SKU fields of product variant in contentful
                        // add sku to ean list if sku is a ean code(length of 13).
                        if (sku.length == 13) {
                            if (!eans.includes(sku)) {
                                eans.push(sku);
                            }
                        }
                    }
                }

                if (productVariant.fields.isBazaarVoiceIdentifier) {
                    reviewAndRatingId = productVariant?.fields?.bazaarVoiceId;
                    // if reviewAndRatingId is null use sku/ean
                    if (reviewAndRatingId === undefined) {
                        if (sku) {
                            reviewAndRatingId = sku;
                        } else if (sku === undefined) {
                            reviewAndRatingId = ean;
                        }
                        if (reviewAndRatingId === undefined) {
                            console.error('No reviewAndRatingId specified for product variant Bazaar Voice identifier:',
                                productVariant?.fields?.name);
                        }
                    }
                }
            }
        }

        // product
        let dccProduct = {
            'productId': reviewAndRatingId,
            'productName': product.name,
            'productDescription': product.productOverview.fields.description,
            'productImageURL': imageUrl,
            'productPageURL': productPageUrl,
            'brandName': OrganizationConstants.brand,
            'upcs' : skus,
            'eans': eans
        }

        if(product.productOverview.fields.reviewAndRatingCategory) {
            dccProduct.categoryPath = [
                {
                    'Name' : product.productOverview.fields.reviewAndRatingCategory
                }
            ]
        }

        // Catalog
        let catalogProducts = [];
        catalogProducts.push(dccProduct);


        // Family and Bazaar voice master product values
        let reviewAndRatingFamilyId = product.productOverview.fields.reviewAndRatingFamilyId;

        if (reviewAndRatingFamilyId) {
            let familyJson = [{
                'id': reviewAndRatingFamilyId,
                'expand': false
            }];
            dccProduct.families = familyJson;
            // Master product
            let masterProductId = getBazaarVoiceMasterProductId(reviewAndRatingFamilyId);
            let masterProductName = getBazaarVoiceMasterProductName(reviewAndRatingFamilyId);

            let masterProductJson = {
                'productId': masterProductId,
                'productName': masterProductName,
                'productPageURL': productPageUrl,
                'upcs' : skus,
                'eans': eans,
                'families': [{
                    'id': reviewAndRatingFamilyId,
                    'expand': true
                }]
            }
            catalogProducts.push(masterProductJson)
        }

        let languageCode = this.props.languageCode;
        let bazaarVoiceLocale =  languageCode + '_' + countryCode;
        let catalogProductsString = customJsonstringify(catalogProducts);

        let render;
        // do not render if we no productID
        if(reviewAndRatingId) {
            render = <Fragment>
                <Script strategy='afterInteractive' dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(`window.bvDCC = {
                    catalogData: {
                            locale: '${bazaarVoiceLocale}',
                            catalogProducts: ${catalogProductsString}
                        }
                    };`),
                }}>
                </Script>
            </Fragment> ;
        }

        return render;
    }

    isValidBarcode(barcode) {
        // check length
        if (barcode.length != 12 && barcode.length != 13) {
            return false;
        }

        let lastDigit = Number(barcode.substring(barcode.length - 1));
        let checkSum;
        if (isNaN(lastDigit)) { return false; } // not a valid upc/ean

        let arr = barcode.substring(0,barcode.length - 1).split('').reverse();
        let oddTotal = 0, evenTotal = 0;

        for (let i=0; i<arr.length; i++) {
            if (isNaN(arr[i])) { return false; } // can't be a valid upc/ean we're checking for

            if (i % 2 == 0) { oddTotal += Number(arr[i]) * 3; }
            else { evenTotal += Number(arr[i]); }
        }
        checkSum = (10 - ((evenTotal + oddTotal) % 10)) % 10;

        // true if they are equal
        return checkSum == lastDigit;
    }

}

export function getBazaarVoiceMasterProductId(reviewAndRatingFamilyId) {
    let masterProductId = BazaarVoiceConstants.masterProductIdPrefix + reviewAndRatingFamilyId;
    return masterProductId;
}

export function getBazaarVoiceMasterProductName(reviewAndRatingFamilyId) {
    let masterProductName = BazaarVoiceConstants.masterProductNamePrefix + reviewAndRatingFamilyId;
    return masterProductName;
}

Product.propTypes = {
    product: PropTypes.object,
    countryCode: PropTypes.string,
    languageCode: PropTypes.string,
    slug: PropTypes.any,
    url: PropTypes.shape({
        asPath: PropTypes.string,
    })
}

export default Product
